import { api } from 'lib/request/api';
import type { WellIntersectionAnalysisResponse } from 'types/api-responses';
import { defaultSelectionsPath } from 'types/wellbore-geometry-workspace';
import {
  VERDICT_TO_KIND,
  type ManualWellInput,
  type StagedWell,
  type WellboreGeometriesResponse,
  type WellsQueryResponse,
  type WellsResultsResponse,
  type WellsSummary,
  type WellWorkspaceDetail,
  type WellWorkspacesListResponse,
  type WellWorkspacesSummary,
  type WorkspaceCompare,
} from 'types/well-workspace';
import { toWorkspaceError } from './utils';
import {
  ltdCandidatesBody,
  type LtdCandidatesQuery,
  type LtdCandidatesResponse,
} from './detail/wellbore/ltd-candidates';

// A well workspace is a WellWorkspace STI row on the generic Workspace model, so it
// uses the reusable `/workspaces` API rather than a bespoke path. Client routes stay
// hyphenated (/well-workspaces) and are unaffected.
const WORKSPACES = 'workspaces';

// Every well workspace is an STI WellWorkspace regardless of kind (property/dca), so the
// listing and summary filter by type, not kind — a DCA workspace must still appear.
const WORKSPACE_TYPE = 'WellWorkspace';

// The reusable Workspace envelope: stable top-level keys with the well-specific fields
// under `detail`, flattened into WellWorkspaceDetail so components read one object.
// `tasks` is a real count, but no endpoint serves the items behind it, so the header
// shows the count and the panel says the list is not available yet.
interface WorkspaceEnvelope {
  href: string;
  auth: string[];
  id: number;
  name: string;
  promoted: boolean;
  promotedAt: string | null;
  promotedBy: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  tasks: { completed: number; total: number };
  resources?: WellWorkspaceDetail['resources'];
  detail: {
    kind: 'property' | 'dca';
    promotable: boolean;
    /** See WellWorkspaceDetail#promotionBlockers. Absent on a backend predating it. */
    promotionBlockers?: string[] | null;
    wells: WellWorkspaceDetail['wells'];
  };
}

/**
 * Unwraps the server's workspace envelope into the shape the app holds.
 * @param {WorkspaceEnvelope} env The envelope as sent.
 * @return {WellWorkspaceDetail} The workspace.
 */
const toDetail = (env: WorkspaceEnvelope): WellWorkspaceDetail => ({
  href: env.href,
  auth: env.auth,
  id: env.id,
  name: env.name,
  kind: env.detail.kind,
  promoted: env.promoted,
  promotedAt: env.promotedAt,
  promotedBy: env.promotedBy,
  promotable: env.detail.promotable,
  promotionBlockers: env.detail.promotionBlockers,
  createdBy: env.createdBy,
  createdAt: env.createdAt,
  updatedAt: env.updatedAt,
  tasks: env.tasks,
  // The envelope's HAL links, carried through rather than dropped: the activity panel
  // reads `resources.events.href` / `resources.comments.href` off this, and renders
  // nothing at all unless BOTH are present.
  resources: env.resources,
  wells: env.detail.wells,
});

/**
 * Every call funnels its failure through toWorkspaceError so React Query's `error` is
 * always a WorkspaceRefused carrying the backend's own reasons where it gave any.
 * @param {Promise<T>} request The request to await.
 * @return {Promise<T>} Its result.
 */
const run = async <T>(request: Promise<T>): Promise<T> => {
  try {
    return await request;
  } catch (error) {
    throw await toWorkspaceError(error);
  }
};

// ── Reads ──
// React Query passes the AbortSignal for the query it is running; handing it to ky is
// what makes a superseded read cancel itself.

export interface ListPaging {
  page: number;
  pageSize: number;
}

// The listing is server-driven end to end: the page, the `name` search and the column
// sort are all resolved in SQL, because one server page is all the browser holds and
// there is nothing else to sort or filter locally. Same param names as the related-items
// and entities listings.
export interface ListQuery extends ListPaging {
  /** Search text; the server matches it against the workspace name. */
  text?: string;
  /** Allowlisted server-side sort column; absent leaves the default order (newest first). */
  sortAttribute?: string;
  sortDirection?: 'ASC' | 'DESC';
  /**
   * Map Query's spatial filter, in the shape `/queries/wells` takes. A workspace qualifies
   * when any member well's surface hole falls inside the shape; the polygon wins when both
   * are present. Absent leaves the listing unbounded.
   */
  boundingBox?: {
    sw_lng: number;
    sw_lat: number;
    ne_lng: number;
    ne_lat: number;
  };
  polygonGeojson?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

/**
 * One page of the listing; the server's envelope carries the total for the pager.
 * @param {ListQuery} query The page, search and sort.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellWorkspacesListResponse>} The page.
 */
export const getWorkspaces = (
  query: ListQuery,
  signal?: AbortSignal
): Promise<WellWorkspacesListResponse> =>
  run(
    api
      .post('queries/workspaces', {
        json: {
          type: WORKSPACE_TYPE,
          page: query.page,
          page_size: query.pageSize,
          // Omitted rather than sent empty: the endpoint falls back to its default
          // order on an absent sort, and an empty `text` would be an ILIKE on '%%'.
          ...(query.text ? { text: query.text } : {}),
          ...(query.sortAttribute
            ? {
                sort_attribute: query.sortAttribute,
                sort_direction: query.sortDirection ?? 'ASC',
              }
            : {}),
          // Only one shape is ever sent. The server prefers the polygon when both arrive,
          // but sending both would leave the request describing two different areas.
          ...(query.polygonGeojson
            ? { polygon_geojson: query.polygonGeojson }
            : query.boundingBox
              ? { bounding_box: query.boundingBox }
              : {}),
        },
        signal,
      })
      .json<WellWorkspacesListResponse>()
  );

/**
 * The listing's summary cards.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellWorkspacesSummary>} The counts.
 */
export const getWorkspacesSummary = (
  signal?: AbortSignal
): Promise<WellWorkspacesSummary> =>
  run(
    api
      .get(`${WORKSPACES}/summary`, {
        searchParams: { type: WORKSPACE_TYPE },
        signal,
      })
      .json<WellWorkspacesSummary>()
  );

/**
 * One workspace, unwrapped from its envelope.
 * @param {string | number} id The workspace.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellWorkspaceDetail>} The workspace.
 */
export const getWorkspace = async (
  id: string | number,
  signal?: AbortSignal
): Promise<WellWorkspaceDetail> =>
  toDetail(
    await run(
      api.get(`${WORKSPACES}/${id}`, { signal }).json<WorkspaceEnvelope>()
    )
  );

// Compare, served off the backend's change-log rather than reconstructed here: only the
// changed wells, each with its field diff and linked documents. Live while a draft and
// frozen once promoted, so it stays truthful after the canonical record moves on.
/**
 * @param {string | number} id The workspace.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WorkspaceCompare>} The before/after comparison.
 */
export const getCompare = (
  id: string | number,
  signal?: AbortSignal
): Promise<WorkspaceCompare> =>
  run(
    api.get(`${WORKSPACES}/${id}/compare`, { signal }).json<WorkspaceCompare>()
  );

// Story 1 wells summary cards. DB-backed, so this resolves even when the ES wells
// search is unavailable.
/**
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellsSummary>} The wells summary cards.
 */
export const getWellsSummary = (signal?: AbortSignal): Promise<WellsSummary> =>
  run(
    api.post('queries/wells/summary', { json: {}, signal }).json<WellsSummary>()
  );

/**
 * One workspace on a well's links row.
 *
 * jbuilder's `key_format` does not deep-format hash values, so a row's own fields can
 * arrive snake_case while the key above it is camelized. Both spellings are declared
 * and {@link linkRow} reads whichever is present.
 */
export interface WellWorkspaceLinkRow {
  id: number;
  name: string;
  promotedAt?: string | null;
  promoted_at?: string | null;
  promotedBy?: string | null;
  promoted_by?: string | null;
  // Added by the backend for the drawer's row detail (INS-10338, `4d36cb321e`).
  // Optional so a backend predating them still serves the four fields above.
  promoted?: boolean;
  createdAt?: string | null;
  created_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
  createdBy?: string | null;
  created_by?: string | null;
}

export interface WellWorkspaceLinkPayload {
  latest: WellWorkspaceLinkRow | null;
  latestPromoted: WellWorkspaceLinkRow | null;
  /**
   * Every workspace this well belongs to, for the drawer, the View menu's counts and
   * the promoted banner. Optional, so a backend predating it still serves the two
   * single-row fields above.
   */
  all?: WellWorkspaceLinkRow[];
}

/** One link row, normalized to camelCase whichever spelling arrived. */
export interface WorkspaceLink {
  id: number;
  name: string;
  promoted: boolean;
  promotedAt: string | null;
  promotedBy: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Normalizes a links row so callers read one spelling.
 * @param {WellWorkspaceLinkRow | null} row The row as served.
 * @return {WorkspaceLink | null} The row, or null when absent.
 */
export const linkRow = (
  row: WellWorkspaceLinkRow | null | undefined
): WorkspaceLink | null => {
  if (!row) return null;

  const promotedAt = row.promotedAt ?? row.promoted_at ?? null;

  return {
    id: row.id,
    name: row.name,
    // The boolean is the backend's answer; a backend predating it is read off the date.
    promoted: row.promoted ?? promotedAt !== null,
    promotedAt,
    promotedBy: row.promotedBy ?? row.promoted_by ?? null,
    createdBy: row.createdBy ?? row.created_by ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
};

/**
 * The well workspaces a well belongs to, keyed by uwi.
 *
 * Serves `latest`, `latestPromoted` and `all` per well, each row with its author and
 * timestamps — everything the Well page's Workspaces drawer and View-menu count read.
 * @param {string[]} uwis The UWIs to look up.
 * @param {AbortSignal} signal Cancels a superseded read.
 * @return {Promise<Record<string, WellWorkspaceLinkPayload>>} Links by uwi.
 */
export const getWellWorkspaceLinks = async (
  uwis: string[],
  signal?: AbortSignal
): Promise<Record<string, WellWorkspaceLinkPayload>> => {
  const body = await run(
    api
      .post('queries/well_workspace_links', { json: { uwis }, signal })
      .json<{ results: Record<string, WellWorkspaceLinkPayload> }>()
  );

  return body.results ?? {};
};

/**
 * Wells listing via the existing Elasticsearch dataset engine.
 * @param {Record<string, unknown>} payload The dataset-engine query.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellsQueryResponse>} The matching wells.
 */
export const getWells = (
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<WellsQueryResponse> =>
  run(
    api
      .post('queries/wells', {
        json: { use_elastic: true, ...payload },
        signal,
      })
      .json<WellsQueryResponse>()
  );

// ── Writes ──
// No signal: a write is not safe to cancel and re-run, so it owns its own lifetime.

/** A workspace opened over wells that already exist in the catalog. */
export interface CreateOverWellsInput {
  wellUwis: string[];
  kind?: 'property' | 'dca';
  name?: string;
}

/**
 * A workspace opened over a single well the operator is authoring (Epic 3 Story 1).
 *
 * There is nothing to select, so no UWIs are sent — the backend seeds the staged well
 * itself, generates its synthetic uwi, and defaults the kind to `property`. Sending
 * `kind` here would be redundant, and sending `well_uwis: []` would read as "a
 * workspace over no wells", which is a different (and refused) request.
 */
export interface CreateBlankWellInput {
  createBlankWell: true;
  name?: string;
}

export type CreateWorkspaceInput = CreateOverWellsInput | CreateBlankWellInput;

/**
 * The create request's body — a workspace over chosen wells, or one blank authored well.
 * @param {CreateWorkspaceInput} input What to create.
 * @return {Record<string, unknown>} The body.
 */
const createBody = (input: CreateWorkspaceInput): Record<string, unknown> => {
  if ('createBlankWell' in input) {
    return { type: WORKSPACE_TYPE, name: input.name, create_blank_well: true };
  }

  return {
    type: WORKSPACE_TYPE,
    well_uwis: input.wellUwis,
    kind: input.kind ?? 'property',
    name: input.name,
  };
};

/**
 * @param {CreateWorkspaceInput} input What to create.
 * @return {Promise<WellWorkspaceDetail>} The new workspace.
 */
export const postWorkspace = async (
  input: CreateWorkspaceInput
): Promise<WellWorkspaceDetail> =>
  toDetail(
    await run(
      api
        .post(WORKSPACES, { json: { workspace: createBody(input) } })
        .json<WorkspaceEnvelope>()
    )
  );

export interface AddWellsInput {
  /** Existing wells to add, by uwi. Bogus uwis are dropped and already-members are a no-op. */
  existingUwis: string[];
  /** New manual wells, each by Well Name + status (the UWI is backend-generated). */
  manual: ManualWellInput[];
}

/**
 * Add a batch of wells to a workspace (Epic 3, Story 3) — existing wells by uwi and/or new manual
 * wells — in ONE request, the body keyed snake_case since only the response is camelized. The
 * backend dedupes an already-member uwi to a no-op and silently drops a uwi that resolves to no
 * well, so the caller sends the staged set as-is. A manual row whose status is missing its
 * required dates 422s and rolls the WHOLE batch back.
 * @param {string | number} workspaceId The workspace.
 * @param {AddWellsInput} input The existing uwis and manual wells to add.
 * @return {Promise<WellsResultsResponse>} The workspace's wells after the add.
 */
export const postWells = (
  workspaceId: string | number,
  input: AddWellsInput
): Promise<WellsResultsResponse> =>
  run(
    api
      .post(`${WORKSPACES}/${workspaceId}/wells`, {
        json: {
          wells: { existing_uwis: input.existingUwis, manual: input.manual },
        },
      })
      .json<WellsResultsResponse>()
  );

/**
 * Remove one well from a workspace (Epic 3, Story 3). `idOrUwi` is the staged-well id or the uwi —
 * the endpoint resolves either — and the well's un-promoted drafts go with it (staged edits, unit
 * picks, wellbore geometry, the freeze verdict), so a removed well cannot resurrect at promote.
 * Removing the LAST well is allowed: the workspace is left empty and `results` comes back `[]`.
 * @param {string | number} workspaceId The workspace.
 * @param {string | number} idOrUwi The staged-well id or the uwi to remove.
 * @return {Promise<WellsResultsResponse>} The workspace's wells after the removal.
 */
export const deleteWell = (
  workspaceId: string | number,
  idOrUwi: string | number
): Promise<WellsResultsResponse> =>
  run(
    api
      .delete(
        `${WORKSPACES}/${workspaceId}/wells/${encodeURIComponent(
          String(idOrUwi)
        )}`
      )
      .json<WellsResultsResponse>()
  );

/**
 * A Well-Status edit as the endpoint takes it: snake_case keys, ISO dates, null to clear.
 * Only `status` and the four operator-writable dates are accepted — the derived production
 * dates have no column to persist and are dropped from the params.
 */
export type WellStatusEdit = Record<string, string | null>;

/**
 * Promotes the workspace, writing its staged edits onto the canonical records.
 * @param {string | number} id The workspace.
 * @return {Promise<WellWorkspaceDetail>} The workspace, now promoted.
 */
export const postPromotion = async (
  id: string | number
): Promise<WellWorkspaceDetail> =>
  toDetail(
    await run(
      api
        .post(`${WORKSPACES}/${id}/promotions`, { json: {} })
        .json<WorkspaceEnvelope>()
    )
  );

/**
 * One well's staged Well-Status edit (Epic 2, Story 2).
 *
 * The operator-facing resource is `wells` — `staged_wells` is storage nomenclature — and the
 * body is keyed snake_case, since only the RESPONSE passes through jbuilder's camelizer.
 * Dates must be exact `YYYY-MM-DD`: the endpoint parses them strictly and 422s anything else
 * rather than coercing a typo into a real date.
 *
 * The backend materializes the staged row copy-on-write, so the first edit to a
 * membership-only workspace is also what makes it promotable.
 *
 * ADDRESSED BY EITHER the staged row's id or the well's uwi — `resolve_well_edit_host`
 * accepts both, and callers should prefer the id when there is one: an authored well's uwi
 * can be typed in later, which would move its address mid-edit (the reason the route is
 * declared `param: :id`). The uwi is not merely a fallback though — a membership-only well
 * has no staged row yet, and addressing it by uwi is what MATERIALIZES one.
 * @param {string | number} workspaceId The workspace.
 * @param {string | number} address The staged row's id, or the well's uwi.
 * @param {WellStatusEdit} edit The status and lifecycle dates, snake_case.
 * @return {Promise<StagedWell>} The staged well as it now stands.
 */
export const patchWellStatus = (
  workspaceId: string | number,
  address: string | number,
  edit: WellStatusEdit
): Promise<StagedWell> =>
  run(
    api
      .patch(
        `${WORKSPACES}/${workspaceId}/wells/${encodeURIComponent(
          String(address)
        )}`,
        { json: { well: edit } }
      )
      .json<StagedWell>()
  );

/**
 * Set a well's Well-Status verdict (Epic 2, Story 2).
 *
 * Rides the shared verification_items events endpoint rather than a workspace route: a
 * verdict is an event on the well's freeze item, and the same endpoint already backs every
 * other verification surface in the app. Marking a well CORRECT and then promoting is what
 * freezes its Well Status against system ingest.
 * `unanswered` clears the verdict — what clicking the verdict a well already carries does,
 * mirroring the Inventory workspace's header. A clear needs no reason; setting one does.
 * @param {number} verificationItemId The well's freeze item, from `wellStatusVerdict`.
 * @param {'correct' | 'incorrect' | 'unanswered'} verdict The verdict being set or cleared.
 * @param {string} reason Why — required by the decision panel, recorded on the event.
 * @return {Promise<void>} Resolves once the event is recorded.
 */
export const postWellStatusVerdict = async (
  verificationItemId: number,
  verdict: 'correct' | 'incorrect' | 'unanswered',
  reason = ''
): Promise<void> => {
  await run(
    api.post(`verification_items/${verificationItemId}/events`, {
      json: { kind: VERDICT_TO_KIND[verdict], reason },
    })
  );
};

/**
 * A well's operator-editable IDENTITY (Epic 3 Story 1).
 *
 * `lease_name` and `well_number` are the two fields the displayed Well Name derives from —
 * there is no name column, so the name itself is never written. `uwi` / `uwi14` are backend-
 * generated and deliberately absent: a user never types them.
 */
export interface WellIdentityEdit {
  lease_name?: string | null;
  well_number?: string | null;
  current_operator?: string | null;
}

/**
 * Save a staged identity edit.
 *
 * Addressed by the STAGED-WELL id rather than the uwi. A well authored in the workspace has
 * only a synthetic uwi, and the route's own comment is explicit that the id is the stable
 * address. (The backend resolves either, which is why the Well-Status editor above still
 * keys on uwi without breaking.)
 * @param {string | number} workspaceId The workspace.
 * @param {number} stagedWellId The staged row being edited.
 * @param {WellIdentityEdit} edit The fields to write, snake_case; null clears one.
 * @return {Promise<StagedWell>} The staged well as it now stands.
 */
export const patchWellIdentity = (
  workspaceId: string | number,
  stagedWellId: number,
  edit: WellIdentityEdit
): Promise<StagedWell> =>
  run(
    api
      .patch(`${WORKSPACES}/${workspaceId}/wells/${stagedWellId}`, {
        json: { well: edit },
      })
      .json<StagedWell>()
  );

/**
 * Operators to offer on the Current Operator field.
 *
 * `POST /wells/field_lookup` is a shared typeahead over the well index — an Elasticsearch
 * terms aggregation on `current_operator.keyword`, prefix-filtered by `text`. Terms
 * aggregations order by document count, so the list arrives most-wells-first, which is the
 * order the mock asks for without anything here having to sort it.
 *
 * Not a fixed enum: operators are whatever the catalog holds, and the field stays free-entry
 * besides (a well built from a document can name one the catalog has never seen).
 * @param {string} text What the reader has typed; empty asks for the most common.
 * @return {Promise<string[]>} The operator names.
 */
export const fetchOperatorOptions = async (text: string): Promise<string[]> => {
  const body = await run(
    api
      .post('wells/field_lookup', {
        json: { field: 'operator', text, limit: 25 },
      })
      .json<{ results?: { value?: string }[] }>()
  );

  return (body.results ?? [])
    .map((entry) => entry.value)
    .filter((value): value is string => Boolean(value));
};

/**
 * Rebuild an existing bore from a new LTD selection, replacing its geometry in place.
 *
 * The same body as the add — the target is named on the PATH, never in the payload, which is
 * what keeps one `toWellboreBody` and one live preview serving both. The bore keeps its
 * completion uwi14 and its geometry row id across the rebuild; its POINTS are deleted and
 * re-inserted, so anything keyed on a wellbore-point id is stale afterwards.
 *
 * Rebuilding the PRIMARY bore also clears the well's staged unit pick and recomputes its
 * geographic fields; rebuilding a secondary leaves both alone. Neither is echoed back and
 * the caller cannot tell which it did, so it re-reads the well either way.
 * @param {string | number} workspaceId The workspace.
 * @param {number} stagedWellId The authored well.
 * @param {string} completionUwi14 The bore being rebuilt — must be one this well has drawn.
 * @param {WellborePayload} wellbore The member sticks and the two designations.
 * @return {Promise<WellboreCreated>} The rebuilt bore and any advisories.
 */
export const patchWellbore = (
  workspaceId: string | number,
  stagedWellId: number,
  completionUwi14: string,
  wellbore: WellborePayload
): Promise<WellboreBuilt> =>
  run(
    api
      .patch(
        `${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/wellbores/${encodeURIComponent(
          completionUwi14
        )}`,
        { json: { wellbore } }
      )
      .json<WellboreBuilt>()
  );

// ── Unit assignment for an authored well (Epic 3 Story 1) ──
// Both calls are keyed by the STAGED-WELL id, not a uwi: they serve a well authored in this
// workspace, which has no canonical twin to look up. They answer with the same envelope as
// `/queries/well_intersection_analysis` plus `bestFit`, so the existing Unit Details table
// renders them unchanged.

/**
 * The units a staged well intersects, for the Units card.
 * @param {number} workspaceId The workspace.
 * @param {number} stagedWellId The staged well.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellIntersectionAnalysisResponse>} The candidates.
 */
export const getWellUnitCandidates = (
  workspaceId: number,
  stagedWellId: number,
  signal?: AbortSignal
): Promise<WellIntersectionAnalysisResponse> =>
  run(
    api
      .get(`${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/units`, {
        signal,
      })
      .json<WellIntersectionAnalysisResponse>()
  );

/**
 * Stage the operator's explicit unit pick.
 *
 * The unit is addressed by its NAME, which is what the assignment keys on — `unitId` in the
 * body is the development area's name, not a numeric id. Answers with the refreshed candidate
 * list, the chosen unit's `assignmentType` now `explicit`, so the caller seeds its cache from
 * the response rather than re-reading.
 * @param {number} workspaceId The workspace.
 * @param {number} stagedWellId The authored well.
 * @param {string} unitName The chosen unit's name.
 * @return {Promise<WellIntersectionAnalysisResponse>} The candidates, re-listed.
 */
export const postUnitAssignment = (
  workspaceId: number,
  stagedWellId: number,
  unitName: string
): Promise<WellIntersectionAnalysisResponse> =>
  run(
    api
      .post(
        `${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/unit_assignment`,
        { json: { unit_id: unitName } }
      )
      .json<WellIntersectionAnalysisResponse>()
  );

// ── Wellbore geometry source (INS-11915) ──
// The Wellbore Geometry Source modal's two calls. Both live at the bore's Well Workspace
// address, so the client never holds a geometry-workspace id for them.

// The geometries the bore can be driven from: its original and promoted rows, plus the
// workspace's own draft because workspace_id is sent. Snake-case keys and the `query`
// envelope, as every sibling listing takes them.
/**
 * @param {number} wellWorkspaceId The workspace.
 * @param {string} uwi14 The bore.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<WellboreGeometriesResponse>} The bore's geometries.
 */
export const getWellboreGeometries = (
  wellWorkspaceId: number,
  uwi14: string,
  signal?: AbortSignal
): Promise<WellboreGeometriesResponse> =>
  run(
    api
      .post('queries/wellbore_geometries', {
        json: { query: { uwi14: [uwi14], workspace_id: wellWorkspaceId } },
        signal,
      })
      .json<WellboreGeometriesResponse>()
  );

/**
 * The bore's default-selections path without its leading slash, since ky prefixes it.
 * @param {number} wellWorkspaceId The workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The path.
 */
const selectionsPath = (wellWorkspaceId: number, uwi14: string): string =>
  defaultSelectionsPath(wellWorkspaceId, uwi14).replace(/^\//, '');

/**
 * Mark one geometry current for the bore.
 * @param {number} wellWorkspaceId The workspace.
 * @param {string} uwi14 The bore.
 * @param {number} geometryId The geometry to make current.
 * @return {Promise<unknown>} The server's acknowledgement.
 */
export const postGeometrySourceSelection = (
  wellWorkspaceId: number,
  uwi14: string,
  geometryId: number
): Promise<unknown> =>
  run(
    api
      .post(selectionsPath(wellWorkspaceId, uwi14), {
        json: { geometry_id: geometryId },
      })
      .json<unknown>()
  );

/**
 * Hand the bore back to the automatic election. The standing pick is named so a stale
 * client cannot clear one somebody else has since made.
 * @param {number} wellWorkspaceId The workspace.
 * @param {string} uwi14 The bore.
 * @param {number} geometryId The pick being cleared.
 * @return {Promise<unknown>} The server's acknowledgement.
 */
export const deleteGeometrySourceSelection = (
  wellWorkspaceId: number,
  uwi14: string,
  geometryId: number
): Promise<unknown> =>
  run(
    api
      .delete(`${selectionsPath(wellWorkspaceId, uwi14)}/${geometryId}`)
      .json<unknown>()
  );

// ── Add Wellbore (Epic 3 Story 1) ──

/** What the Add Wellbore save sends: the inputs, not the client's derived trajectory. */
export interface WellborePayload {
  ltd_slot_ids: number[];
  surface_hole: { slot_id: number; end: string };
  bottom_hole: { slot_id: number; end: string };
}

/**
 * The staged composite wellbore, as both the add and the rebuild return it.
 *
 * The well's recomputed fields (hole direction, state, county, SIM landing zone, perforated
 * length) are NOT echoed here. They used to be, under a `recalculated` key; the backend
 * dropped it so that one place answers for them — the well endpoint — rather than the card
 * having to decide whether a response's copy or the well's was newer. Both savers
 * invalidate the well, which is what repaints the cards.
 */
export interface WellboreBuilt {
  wellbore: {
    completionUwi14: string;
    wellboreGeometryId: number;
    geometrySource: string;
    perforatedLateralLengthFt: number | null;
  };
  /** Non-blocking advisories — an over-long derived tie, a stick with no perforated length. */
  warnings: string[];
}

/**
 * Build a (composite) wellbore for a staged well from selected LTD sticks.
 *
 * Nested under the well because a created well has no uwi14 until the bore exists, so the
 * well context has to be on the path; addressed by the stable staged-well id.
 * @param {string | number} workspaceId The workspace.
 * @param {number} stagedWellId The authored well.
 * @param {WellborePayload} wellbore The member sticks and the two designations.
 * @return {Promise<WellboreBuilt>} The bore and any advisories.
 */
export const postWellbore = (
  workspaceId: string | number,
  stagedWellId: number,
  wellbore: WellborePayload
): Promise<WellboreBuilt> =>
  run(
    api
      .post(`${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/wellbores`, {
        json: { wellbore },
      })
      .json<WellboreBuilt>()
  );

/** What a bore-structure change reports back: the bores that remain, and which one leads. */
export interface BoreChange {
  /** The bore the well's fields derive from. Null once the last bore is gone. */
  primaryCompletionUwi14: string | null;
  /** Every bore the well still has drawn, lowest completion suffix first. */
  boreCompletionUwi14s: string[];
  /** The bore that was just removed. */
  removedCompletionUwi14: string;
}

/**
 * Remove one drawn bore from a staged well.
 *
 * Any drawn bore may go, INCLUDING THE LAST ONE — which leaves a boreless, non-promotable
 * well rather than an error. Only unpromoted rows are touched: the bore's staged geometry,
 * its points, its source pick and its geometry-workspace bridge row, that last one being
 * what lets the same completion suffix be rebuilt cleanly afterwards.
 *
 * Removing the PRIMARY repoints it at the lowest surviving bore, clears the well's staged
 * unit pick and recomputes (or clears) its geographic fields; removing a secondary leaves
 * all three alone. The response carries only the bore STRUCTURE, so the caller re-reads the
 * well either way rather than trying to tell which case it got.
 * @param {string | number} workspaceId The workspace.
 * @param {number} stagedWellId The authored well.
 * @param {string} completionUwi14 The bore to remove — must be one this well has drawn.
 * @return {Promise<BoreChange>} The bores that remain and which one now leads.
 */
export const deleteWellbore = (
  workspaceId: string | number,
  stagedWellId: number,
  completionUwi14: string
): Promise<BoreChange> =>
  run(
    api
      .delete(
        `${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/wellbores/${encodeURIComponent(
          completionUwi14
        )}`
      )
      .json<BoreChange>()
  );

/**
 * Make an already-drawn bore the well's primary.
 *
 * ONLY THE POINTER MOVES — no identifier changes and no geometry is rewritten. The well's
 * staged unit pick clears and its geographic fields recompute from the new primary's
 * surface hole, neither of which is echoed back, so the caller re-reads the well.
 * @param {string | number} workspaceId The workspace.
 * @param {number} stagedWellId The authored well.
 * @param {string} completionUwi14 The bore to promote — must be one this well has drawn.
 * @return {Promise<BoreChange>} The bores and which one now leads.
 */
export const patchPrimaryWellbore = (
  workspaceId: string | number,
  stagedWellId: number,
  completionUwi14: string
): Promise<BoreChange> =>
  run(
    api
      .patch(
        `${WORKSPACES}/${workspaceId}/wells/${stagedWellId}/wellbores/${encodeURIComponent(
          completionUwi14
        )}/primary`
      )
      .json<BoreChange>()
  );

// ── Add Wellbore: the LTD catalog (the client call moved here from ltd-candidates.ts,
// which keeps the facets, the query shape and the body builder) ──

/**
 * One page of LTD candidates for the Add Wellbore picker.
 * @param {LtdCandidatesQuery} query The filters, paging and sort.
 * @param {AbortSignal} [signal] Abort signal.
 * @return {Promise<LtdCandidatesResponse>} The page.
 */
export const getLtdCandidates = (
  query: LtdCandidatesQuery,
  signal?: AbortSignal
): Promise<LtdCandidatesResponse> =>
  run(
    api
      .post('queries/ltd_candidates', {
        json: { query: ltdCandidatesBody(query) },
        signal,
      })
      .json<LtdCandidatesResponse>()
  );
